import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Paper, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Grid, 
  CircularProgress,
  InputAdornment,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import ProjectSelector from '../shared/ProjectSelector';

interface DepositModalProps {
  open: boolean;
  onClose: () => void;
  cashboxId: string;
  cashboxName: string;
  currency: string;
  companyId: string;
  currencySymbol: string;
  balance: number;
  onSuccess?: () => void;
}

const DepositModal: React.FC<DepositModalProps> = ({
  open,
  onClose,
  cashboxId,
  cashboxName,
  currency,
  companyId,
  currencySymbol,
  balance,
  onSuccess
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [amount, setAmount] = useState<string>('');
  const [rawAmount, setRawAmount] = useState<string>(''); // Formatlanmamış değer
  const [description, setDescription] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);
  // ✅ Geçmiş tarihli işlem: işaretli ve tarih girilmişse hareket kaydı TAMAMEN o tarihe yazılır (Date + CreatedAt).
  const [isBackdated, setIsBackdated] = useState<boolean>(false);
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  // Modal kapanınca formu sıfırla
  useEffect(() => {
    if (!open) {
      setAmount('');
      setRawAmount('');
      setDescription('');
      setError(null);
      setIsBackdated(false);
      setProjectId(null);
      const today = new Date();
      setTransactionDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
    }
  }, [open]);

  // Türk formatında sayı formatlama (1.000,00)
  const formatTurkishNumber = (value: string): string => {
    if (!value) return '';
    
    // Sadece rakam ve virgül al
    const cleanValue = value.replace(/[^0-9,]/g, '');
    
    // Virgül varsa ayır
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    // Tam kısmı bin ayraçlarıyla formatla
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Ondalık kısmı max 2 basamak
    const limitedDecimal = decimalPart.slice(0, 2);
    
    // Birleştir
    if (cleanValue.includes(',')) {
      return `${formattedInteger},${limitedDecimal}`;
    }
    
    return formattedInteger;
  };

  // Formatı temizle ve sayıya çevir
  const parseFormattedNumber = (value: string): number => {
    if (!value) return 0;
    // Nokta (bin ayracı) kaldır, virgül (ondalık) nokta yap
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Sayısal girdi kontrolü ve formatlama
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Sadece rakam, virgül ve nokta kabul et
    if (value && !/^[0-9.,]*$/.test(value)) {
      return;
    }
    
    // Birden fazla virgül olmasını engelle
    if ((value.match(/,/g) || []).length > 1) {
      return;
    }
    
    // Virgülden sonra 2 basamaktan fazla olmasını engelle
    if (value.includes(',')) {
      const parts = value.split(',');
      if (parts[1] && parts[1].length > 2) {
        return;
      }
    }
    
    // Raw değeri kaydet
    setRawAmount(value.replace(/\./g, '')); // Noktaları kaldır (bin ayracı)
    
    // Formatlanmış değeri göster
    setAmount(formatTurkishNumber(value));
  };

  // Para yatırma işlemi (SQL API)
  const handleDeposit = async () => {
    // Formatlanmış değeri sayıya çevir
    const numericAmount = parseFormattedNumber(amount);
    
    // Validasyon
    if (!amount || numericAmount <= 0) {
      setError(tt('Lütfen geçerli bir miktar giriniz.', 'Please enter a valid amount.'));
      return;
    }

    // ✅ Geçmiş tarihli işlem: işaretliyse seçilen tarihi hesapla.
    // Hareket kaydı TAMAMEN bu tarihe yazılacak (backend Date + CreatedAt'i bu değere ayarlar).
    let backdatedIso: string | null = null;
    if (isBackdated) {
      const parts = transactionDate.split('-');
      if (parts.length !== 3) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        return;
      }
      // Gün kayması yaşanmaması için öğlen (12:00) sabitlenir.
      const selected = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
      if (isNaN(selected.getTime())) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        return;
      }
      backdatedIso = selected.toISOString();
    }

    setSaving(true);
    setError(null);

    try {
      console.log('💰 Para yatırma işlemi başlatılıyor (SQL)...');
      
      // Kasa ID'yi sayıya çevir
      const numericCashboxId = parseInt(cashboxId, 10);
      if (isNaN(numericCashboxId)) {
        throw new Error(tt('Geçersiz kasa ID formatı', 'Invalid cashbox ID format'));
      }
      
      const depositPayload: Record<string, any> = {
          amount: numericAmount,
          description: description.trim() || tt(`Kasaya para yatırma: ${cashboxName}`, `Deposit to cashbox: ${cashboxName}`),
          createdBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      // Yalnızca geçmiş tarihli işlem seçildiyse tarihi gönder; aksi halde backend bugünü kullanır.
      if (backdatedIso) {
        depositPayload.transactionDate = backdatedIso;
      }
      if (projectId) {
        depositPayload.projectId = projectId;
      }

      // API'ye para yatırma isteği gönder
      const response = await accountingApi.post(ACCOUNTING_API.CASHBOX_DEPOSIT(numericCashboxId), depositPayload);
      const result = response.data;
      console.log('✅ Para yatırma başarılı:', result);
      
      // Başarıyla tamamlandı
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('❌ Para yatırma işlemi sırasında hata oluştu:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : tt('Para yatırma işlemi sırasında bir hata oluştu.', 'An error occurred during deposit.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={!saving ? onClose : undefined}
      aria-labelledby="para-yatirma-modal"
    >
      <Paper 
        sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 450 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" fontWeight="bold" color="primary">
            {tt('Para Yatırma', 'Deposit')}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {tt('Kasa', 'Cashbox')}: <strong>{cashboxName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {tt('Mevcut Bakiye', 'Current Balance')}: <strong>{balance.toLocaleString(localeCode)} {currencySymbol}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            {tt('Bu işlemle kasaya', 'With this transaction, you can deposit')} <strong>{currency}</strong> {tt('para biriminde para yatırabilirsiniz.', 'into the cashbox.')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              required
              fullWidth
              label={tt('Yatırılacak Miktar', 'Deposit Amount')}
              value={amount}
              onChange={handleAmountChange}
              type="text"
              placeholder={tt('0,00', '0.00')}
              InputProps={{
                endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment>,
              }}
              error={!!error && !amount}
              helperText={!amount && !!error ? tt('Bu alan zorunludur', 'This field is required') : tt('Örnek: 1.000,00', 'Example: 1,000.00')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={tt('Açıklama', 'Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              placeholder={tt('İsteğe bağlı açıklama ekleyebilirsiniz', 'You can add an optional description')}
            />
          </Grid>
          <Grid item xs={12}>
            <ProjectSelector value={projectId} onChange={setProjectId} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isBackdated}
                  onChange={(e) => setIsBackdated(e.target.checked)}
                  disabled={saving}
                  color="primary"
                />
              }
              label={tt('Geçmiş tarihli işlem yap', 'Record as a back-dated transaction')}
            />
            {isBackdated && (
              <>
                <TextField
                  label={tt('İşlem Tarihi *', 'Transaction Date *')}
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  fullWidth
                  required
                  disabled={saving}
                  sx={{ mt: 1, mb: 1 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`
                  }}
                />
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {tt(
                    'Bu işlem seçtiğiniz tarihe kaydedilecektir. İşlem kaydı tamamen o tarihe ait olacak; bugüne dair hiçbir bilgi içermeyecektir.',
                    'This transaction will be recorded on the selected date. The record will fully belong to that date and contain no reference to today.'
                  )}
                </Alert>
              </>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
            disabled={saving}
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleDeposit}
            disabled={!amount || saving || parseFormattedNumber(amount) <= 0 || (isBackdated && !transactionDate)}
            startIcon={saving ? <CircularProgress size={24} /> : null}
          >
            {saving ? tt('İşleniyor...', 'Processing...') : tt('Para Yatır', 'Deposit')}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default DepositModal; 